import { appConfig } from '@/config/app.config';
import { reduxPersistor } from '@/lib';

import type { ITeamMembersRole, ITemMemberPermission, ITransactionStatus, TargetEnvTypes } from '../types';

const TIME_FORMAT = 'hh:m a';
const DATE_FORMAT = 'MMM, dd yyyy';
const FULL_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss zzz';
const FULL_DATE_TIME_FORMAT_ISO_Z = "yyyy-MM-dd'T'HH:mm:ss.SSSX";
const FULL_DATE_TIME_FORMAT2 = 'MMMM d yyyy, h:mm aa';
const CALENDAR_DATE_TIME_FORMAT = 'MMMM d, yyyy h:mm aa';
export const MS_IN_SECOND = 1000;
export const MS_IN_TEN_SECONDS = MS_IN_SECOND * 10;
export const MS_IN_MINUTE = MS_IN_SECOND * 60;
export const MS_IN_HOUR = MS_IN_MINUTE * 60;
export const MS_IN_DAY = MS_IN_HOUR * 24;

export const dateConst = {
	TIME_FORMAT,
	DATE_FORMAT,
	FULL_DATE_TIME_FORMAT,
	FULL_DATE_TIME_FORMAT_ISO_Z,
	FULL_DATE_TIME_FORMAT2,
	CALENDAR_DATE_TIME_FORMAT,
};

// The two hosts served against real backends. Everything else — localhost and any other preview/dev URL —
// is a dev environment locked to sandbox: `getTargetEnvironment` forces sandbox and the Live/Sandbox toggle
// is disabled, because on dev there is no separate live backend to switch to.
const PRODUCTION_HOST = 'partners.breet.io';
const STAGING_HOST = 'staging.partners.breet.io';
const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
const isProductionHost = currentHost === PRODUCTION_HOST;

export const isDevEnvironment = (): boolean =>
	typeof window !== 'undefined' && currentHost !== PRODUCTION_HOST && currentHost !== STAGING_HOST;

// Per environment: production → live=production / sandbox=sandbox; staging → live=staging / sandbox=development;
// dev → sandbox=development (live is unused there but kept distinct so `isSandboxEnvironment` reads true).
export const LIVE_ENVIRONMENT: TargetEnvTypes = isProductionHost ? 'production' : 'staging';

export const SANDBOX_ENVIRONMENT: TargetEnvTypes = isProductionHost ? 'sandbox' : 'development';

export const isSandboxEnvironment = (env: TargetEnvTypes | undefined): boolean => env !== undefined && env !== LIVE_ENVIRONMENT;

export const getTargetEnvironment = (): TargetEnvTypes => {
	if (isDevEnvironment()) {
		return !appConfig.disableEnvLock ? SANDBOX_ENVIRONMENT : LIVE_ENVIRONMENT;
	}
	return isSandboxEnvironment(reduxPersistor.getData('app')?.targetEnvironment) ? SANDBOX_ENVIRONMENT : LIVE_ENVIRONMENT;
};

export const baseApiHeaders = {
	get 'x-breet-env'() {
		return getTargetEnvironment();
	},
};

export const HIDE_TOAST_KEY = 'x-hide-toast';

export const SHOW_TOAST_KEY = 'x-show-toast';

export const ENV_TARGET_KEY = 'x-breet-env-target';

export const KYBSTATUSES = {
	COMPLETED: 'completed',
	REJECTED: 'rejected',
	PARTIAL: 'partial',
	PENDING: 'pending',
} as const;

export const CURRENCYCONFIG = {
	NGN: { country: 'Nigeria', locale: 'en-NG', symbol: '₦', currency: 'nigerian naira', currencyShort: 'naira', code: 'NGN' },
	GHS: { country: 'Ghana', locale: 'en-GH', symbol: '₵', currency: 'ghanaian cedi', currencyShort: 'cedi', code: 'GHS' },
	USD: {
		country: 'United States of America',
		locale: 'en-US',
		symbol: '$',
		currency: 'us dollar',
		currencyShort: 'dollar',
		code: 'USD',
	},
} as const;

export const CURRENCIES: CURRENCYCODETYPE[] = Object.values(CURRENCYCONFIG).map((config) => config.code);

export type CURRENCYCONFIGTYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG];

export type CURRENCYCODETYPE = keyof typeof CURRENCYCONFIG;

export type CURRENCYSYMBOLTYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['symbol'];

export type CURRENCYNAMETYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['currency'];

export type COUNTRYTYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['country'];

export type LOCALETYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['locale'];

export type CURRENCYSHORTTYPE = (typeof CURRENCYCONFIG)[keyof typeof CURRENCYCONFIG]['currencyShort'];

export const SORT_LATEST_TO_OLDEST = -1;
export const SORT_OLDEST_TO_LATEST = 1;

export const INACTIVITY_TOTAL_DURATION = 5 * 60 * 1000;
export const INACTIVITY_WARNING_OFFSET = 1 * 60 * 1000;

export const ISPRODUCTION = typeof window !== 'undefined' ? window.location.origin === 'https://partners.breet.io' : false;

export type MENUICONTYPE =
	| 'dashboard'
	| 'wallet'
	| 'addresses'
	| 'transaction'
	| 'withdrawals'
	| 'analytics'
	| 'developer'
	| 'settings'
	| 'logout';
export type MENUTTITLEYPE =
	| 'Dashboard'
	| 'Wallet'
	| 'Addresses'
	| 'Transactions'
	| 'Withdrawals'
	| 'Analytics'
	| 'Developer'
	| 'Settings'
	| 'Log Out'
	| 'Notifications';

export const POSTMAN_DOCUMENTATION = 'https://docs.breet.io/';

export const FXRATE = {
	DEFAULT: 'default',
	BUY: 'buy',
} as const;

export const FXCONVERSION = {
	NGNToGHS: 'NGNToGHS',
	GHSToNGN: 'GHSToNGN',
	USDToNGN: 'USDToNGN',
	USDToGHS: 'USDToGHS',
	SELLUSD: 'SellUSD',
	FIATToUSD: 'FiatToUSD',
} as const;

export const OTPLENGTH = 6;
export const PINLENGTH = 4;

export const RECOVERY_CODES_MAX = 10;

export const TWOFAWARNINGTHRESHOLD = {
	NGN: 20000000,
	GHS: 100000,
	USD: 10000,
};

export const HTTPERROR = {
	EMAIL: 'please enter email otp',
	AUTHENTICATOR: 'please enter authenticator otp',
	OTPSENT: 'OTP authentication details sent to the associated email',
	RESETLINKEXPIRED: 'reset link is invalid or has expired. Please request a new one.',
};

export const HTTPSTATUSCODE = {
	TWOFA: 202,
	NEWDEVICE: 206,
	TRANSACTIONNOAUTH: 403,
	MULTIPLETEAMS: 300,
	ACCEPTTEAMINVITE: 422,
	EXPIREDTEAMINVITATION: 400,
};

export const FIXEDCACHEKEYS = {
	SAVE_WALLET_ADDRESS: 'saveWalletAddress',
	LOGIN: 'login',
} as const;

export const COMPLETEKYBPATH = '/complete-kyb';
export const ACCEPTINVITEPATH = '/accept-invite';
export const VERIFYEMAILPATH = '/verify-email';
export const RESETPASSWORDPATH = '/reset-password';
export const permissionOptions: Record<
	ITemMemberPermission,
	{ permission: ITemMemberPermission; label: string; description: string }
> = {
	export: {
		label: 'Export Data',
		permission: 'export',
		description: 'Export transaction data.',
	},
	generate_secret_prod: {
		label: 'Generate Production Secret',
		permission: 'generate_secret_prod',
		description: 'Generate and reset Live Production API keys.',
	},
	generate_secret_sandbox: {
		label: 'Generate Sandbox Secret',
		permission: 'generate_secret_sandbox',
		description: 'Generate and reset Sandbox API keys.',
	},
	manage_team: {
		label: 'Manage Team',
		permission: 'manage_team',
		description: 'Invite, edit, or remove team members.',
	},
	manage_pin: {
		label: 'Manage PIN',
		permission: 'manage_pin',
		description: 'Manage PIN for transactions.',
	},
	read: {
		label: 'View Access Only',
		permission: 'read',
		description: 'View dashboards, wallets, addresses, transactions and analytics.',
	},
	settings: {
		label: 'Settings',
		permission: 'settings',
		description: 'View and manage workspace settings.',
	},
	payments: {
		label: 'Payments',
		permission: 'payments',
		description: 'Make payments and execute withdrawals.',
	},
};

export const selectedTeamDosDonts: Record<ITeamMembersRole, Record<'dos' | 'donts', string[]>> = {
	owner: {
		dos: [
			'View and access all pages across the Business Dashboard.',
			'Make payments and execute withdrawals.',
			'Export transaction data.',
			'Generate and reset Sandbox API keys.',
			'Generate and reset Live Production API keys.',
			'Generate and reset Webhook Secret keys.',
			'Create, update or reset the business transaction PIN.',
			'Manage all business settings, including Crypto Settings and Automatic Settlement.',
			'Manage personal account settings.',
			'Invite, edit and delete other team members',
			'Invite, edit or delete Super Admins.',
		],
		donts: ['Edit or delete the Owner account.'],
	},
	super_admin: {
		dos: [
			'View dashboards, wallets, addresses, transactions and analytics.',
			'Make payments and execute withdrawals.',
			'Export transaction data.',
			'Generate and reset Sandbox API keys.',
			'Generate and reset Live Production API keys.',
			'Generate and reset Webhook Secret keys.',
			'Manage business settings.',
			'Manage personal account settings.',
			'Invite, edit and delete other team members',
		],
		donts: [
			'Create, update or reset the business transaction PIN.',
			'Invite, edit or delete Super Admins.',
			'Edit or delete the Owner account.',
		],
	},
	subadmin: {
		dos: [
			'View dashboards, wallets, addresses, transactions and analytics.',
			'Export transaction data.',
			'Generate and reset Sandbox API keys.',
			'View and manage Developer Integrations.',
			'Manage business settings. ',
			'Manage personal account settings.',
		],
		donts: [
			'Make payments or withdrawals.',
			'Generate or reset Live Production API keys.',
			'Generate or reset Webhook Secret keys.',
			'Create, update or reset the business transaction PIN.',
			'Manage team members.',
		],
	},
	customer_support: {
		dos: ['View dashboards, wallets, addresses, transactions and analytics.', 'Manage personal account settings.'],
		donts: [
			'Make payments or withdrawals.',
			'Export transaction data.',
			'Access Developer Integrations.',
			'Generate or reset Sandbox API keys.',
			'Generate or reset Live Production API keys.',
			'Generate or reset Webhook Secret keys.',
			'Create, update or reset the business transaction PIN.',
			'Manage team members.',
		],
	},
	developer: {
		dos: [
			'View dashboards, wallets, addresses, transactions and analytics.',
			'Generate and reset Sandbox API keys.',
			'Access Developer Integrations.',
			'Manage personal account settings.',
		],
		donts: [
			'Make payments or withdrawals.',
			'Export transaction data.',
			'Generate or reset Live Production API keys.',
			'Generate or reset Webhook Secret keys.',
			'Create, update or reset the business transaction PIN.',
			'Manage team members.',
		],
	},
};

export const TEAM_MEMBER_ROLE_AND_PERMISSIONS: Record<
	ITeamMembersRole,
	{ permission: ITemMemberPermission; label: string; description: string }[]
> = {
	customer_support: [permissionOptions.read],
	developer: [permissionOptions.read, permissionOptions.generate_secret_sandbox],
	subadmin: [
		permissionOptions.read,
		permissionOptions.export,
		permissionOptions.generate_secret_sandbox,
		permissionOptions.settings,
	],
	owner: [
		permissionOptions.read,
		permissionOptions.payments,
		permissionOptions.generate_secret_sandbox,
		permissionOptions.generate_secret_prod,
		permissionOptions.manage_team,
		permissionOptions.export,
		permissionOptions.settings,
		permissionOptions.manage_pin,
	],
	super_admin: [
		permissionOptions.read,
		permissionOptions.payments,
		permissionOptions.generate_secret_sandbox,
		permissionOptions.generate_secret_prod,
		permissionOptions.manage_team,
		permissionOptions.export,
		permissionOptions.settings,
	],
};

export const ALL_TEAM_MEMBER_PERMISSIONS = Object.keys(permissionOptions) as ITemMemberPermission[];

export const permissionsForRole = (role: ITeamMembersRole): ITemMemberPermission[] =>
	((TEAM_MEMBER_ROLE_AND_PERMISSIONS as Partial<typeof TEAM_MEMBER_ROLE_AND_PERMISSIONS>)[role] ?? []).map(
		(item) => item.permission
	);

export const resolveTeamPermissions = (user?: { businessRole?: ITeamMembersRole }): ITemMemberPermission[] =>
	user?.businessRole ? permissionsForRole(user.businessRole) : [];

export const isBusinessOwner = (user?: { businessRole?: ITeamMembersRole }): boolean => user?.businessRole === 'owner';

export const BUSINESS_NAME_MIN = 4;
export const BUSINESS_NAME_MAX = 50;
export const BUSINESS_DESCRIPTION_MIN = 10;
export const BUSINESS_DESCRIPTION_MAX = 300;

export const NARRATION_MAX = 32;

export const URL_MAX = 255;
export const NUBAN_ACCOUNT_NUMBER_LENGTH = 10;
export const ACCOUNT_NUMBER_MAX_LENGTH_FALLBACK = 20;

export const EMAIL_MAX_LENGTH = 255;
export const DEFAULT_INPUT_MAX_LENGTH = 32;

export const MAX_DIRECTORS_ALLOWED = 3;

export const TRANSACTION_STATUS_TIPS: Record<ITransactionStatus, string> = {
	completed: 'Finished successfully. Funds are settled in the wallet.',
	deleted: 'Removed or discarded. No processing or settlement occurred.',
	failed: 'Unsuccessful transaction. No funds were debited or moved.',
	flagged: 'Held for review. Funds are not available until it is cleared.',
	pending: 'Received and waiting to finish. Not final yet.',
	processing: 'Actively being handled now. Final status comes next.',
	reversed: 'Failed after an attempt to send. Funds were returned to the wallet.',
	rejected: 'Blocked before it was sent. Funds were returned to the wallet.',
};

export const CHATWOOT_ARTICLE_LINKS: Record<string, string> = {
	12621462: 'https://app.chatwoot.com/hc/breet/articles/12621462-what-is-automatic-settlement',
	15544716:
		'https://app.chatwoot.com/hc/breet/articles/15544716-secure-your-business-dashboard-with-multi-factor-authentication-mfa',
	15014973: 'https://app.chatwoot.com/hc/breet/articles/15014973-how-does-crypto-funding-work',
	15391910: 'https://app.chatwoot.com/hc/breet/articles/15391910-how-to-use-recovery-codes',
	15830373: 'https://app.chatwoot.com/hc/breet/articles/15830373-how-to-create-and-fund-your-business-virtual-bank-account',
	11856241: 'https://app.chatwoot.com/hc/breet/articles/11856241-how-to-top-up-a-flagged-transaction',
	11856536: 'https://app.chatwoot.com/hc/breet/articles/11856536-how-to-pay-the-resolution-fee-for-a-flagged-transaction',
	15830205: 'https://app.chatwoot.com/hc/breet/articles/15830205-how-can-i-fund-my-business-wallet',
	13925878: 'https://app.chatwoot.com/hc/breet/articles/13925878-how-do-i-set-my-default-settlement-wallet',
	15947563: 'https://app.chatwoot.com/hc/breet/articles/15947563-how-do-currency-conversions-work',
	15944852: 'https://app.chatwoot.com/hc/breet/articles/15944852-how-can-i-withdraw-funds-to-my-business-bank-account',
	15945141: 'https://app.chatwoot.com/hc/breet/articles/15945141-how-to-send-stablecoins-from-the-business-dashboard',
};
